
import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../lib/auth.context'

// Define public routes that don't require authentication
const publicPaths = [
  '/auth/reset-password',
  '/auth/forgot-password',
  '/auth/login',
  '/auth/complete-invite'
];

interface ProtectedRouteProps {
  children: React.ReactNode
  requireAdmin?: boolean
}

export function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { isAuthenticated, user, isBanned, bannedUntil, isTOTPVerified } = useAuth()
  const location = useLocation()

  // Show banned user message if user is banned
  if (isBanned && bannedUntil) {
    const BannedUserMessage = React.lazy(() => import('../admin/users/banned-user-message').then(m => ({ default: m.BannedUserMessage })))
    return (
      <React.Suspense fallback={<div>Loading...</div>}>
        <BannedUserMessage bannedUntil={bannedUntil} />
      </React.Suspense>
    )
  }
  
  // Allow access to public paths without authentication
  const currentPath = location.pathname.toLowerCase()
  if (publicPaths.some(path => currentPath === path || currentPath.startsWith(path + '/'))) {
    return <>{children}</>
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }

  // Check if TOTP verification is required and not completed
  // For authenticated users, always require TOTP verification except on auth pages
  if (isAuthenticated && !isTOTPVerified && !currentPath.startsWith('/auth/')) {
    console.log('[ProtectedRoute] TOTP not verified for authenticated user, redirecting to login. Path:', currentPath)
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }

  // Check if admin access is required and user has admin role
  if (requireAdmin && user?.user_metadata?.role !== 'admin') {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
